import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Check, X, AlertCircle, Zap, Search, Banknote, Wifi, Pencil, Copy, Clock, MessageSquare, ChevronLeft, ChevronRight, Star, Coins, TrendingUp, DollarSign, GraduationCap, User, CreditCard, Shield, Award, Users, Trash2 } from "lucide-react";
import axios from "axios";
import { offlinePost, offlinePut } from "@/lib/offlineApi";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CLASS_TYPES = ["All Classes", "Tigers Kung Fu", "Dragons Kung Fu", "Adults Kung Fu", "Tai Chi"];

// v12.4.0: Fee category tabs
const FEE_CATEGORIES = [
  { key: "Lesson", label: "Lesson Fees", icon: "Banknote", color: "#22c55e" },
  { key: "Membership", label: "Membership & Insurance", icon: "Shield", color: "#f97316" },
  { key: "Grading", label: "Grading Fees", icon: "Award", color: "#a855f7" },
];

export default function Payments() {
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  
  // Add Payment Form States
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  
  // Edit Payment States
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentDate, setEditPaymentDate] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState("");
  const [editAmount, setEditAmount] = useState("");
  
  // Payment status toggle for Record Payment modal
  const [paymentStatus, setPaymentStatus] = useState("paid");
  // v7.4: Private Lesson toggle
  const [isPrivateLesson, setIsPrivateLesson] = useState(false);

  // Reminders tab
  const [activeTab, setActiveTab] = useState("ledger");
  const [reminderThreshold, setReminderThreshold] = useState(7);
  
  // v12.4.0: Fee Category Tab
  const [feeCategory, setFeeCategory] = useState("Lesson");
  
  // Filter States — v7.2: default to current month
  const [navMonth, setNavMonth] = useState(new Date().getMonth() + 1);
  const [navYear, setNavYear] = useState(new Date().getFullYear());
  // v7.3: Quick-Jump grid overlay
  const [showQuickJump, setShowQuickJump] = useState(false);
  const [quickJumpYear, setQuickJumpYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClass, setFilterClass] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Bulk generate states
  const [bulkMonth, setBulkMonth] = useState((new Date().getMonth() + 1).toString());
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear().toString());
  const [bulkClassType, setBulkClassType] = useState("All Classes");
  const [bulkMarkAsPaid, setBulkMarkAsPaid] = useState(false);
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState("cash");

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      setPaymentDate(today);
    }
  }, [dialogOpen, today]);

  const fetchData = async () => {
    try {
      const [studentsRes, paymentsRes, thresholdRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/payments/unified`),
        axios.get(`${API}/settings/reminder_threshold_days`).catch(() => ({ data: { value: null } })),
      ]);
      setStudents(studentsRes.data);
      
      // Filter out payments for deleted students
      const studentIds = new Set(studentsRes.data.map(s => s.id));
      const validPayments = (paymentsRes.data || []).filter(p => studentIds.has(p.student_id));
      setPayments(validPayments);
      
      if (thresholdRes.data?.value) setReminderThreshold(Number(thresholdRes.data.value) || 7);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDateForStorage = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Format date from DD/MM/YYYY to display format
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "—";
    return dateStr;
  };

  const handleCreatePayment = async () => {
    if (!selectedStudent || !selectedMonth || !selectedYear || !amount) {
      toast.error("Please fill in Student, Month, Year, and Amount");
      return;
    }
    
    const isPaidRecord = paymentStatus === "paid";
    
    if (isPaidRecord && !paymentDate) {
      toast.error("Please select the payment date");
      return;
    }
    if (isPaidRecord && !paymentMethod) {
      toast.error("Please select Cash or Online");
      return;
    }

    try {
      const res = await offlinePost('/payments', {
        student_id: selectedStudent,
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear),
        amount: parseFloat(amount),
        status: paymentStatus,
        payment_date: isPaidRecord ? formatDateForStorage(paymentDate) : null,
        payment_method: isPaidRecord ? paymentMethod : null,
        is_private_lesson: isPrivateLesson,
        category: feeCategory,
      });
      if (res.data.offline) {
        toast.info("Payment saved offline — will sync when connected");
      } else {
        toast.success(isPaidRecord ? "Payment recorded successfully" : "Unpaid record created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.error || error.message || "Failed to create payment";
      toast.error(detail);
      console.error("Payment create error:", error.response?.data || error.message);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentDate(today);
    setEditPaymentMethod(payment.payment_method || "");
    setEditAmount(payment.amount?.toString() || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editPaymentDate) {
      toast.error("Please select the payment date");
      return;
    }
    if (!editPaymentMethod) {
      toast.error("Please select Cash or Online");
      return;
    }

    try {
      const updateData = {
        status: "paid",
        payment_date: formatDateForStorage(editPaymentDate),
        payment_method: editPaymentMethod,
      };
      if (editAmount && parseFloat(editAmount) > 0) {
        updateData.amount = parseFloat(editAmount);
      }
      await offlinePut(`/payments/${editingPayment.id}`, updateData);
      toast.success("Payment updated");
      setEditDialogOpen(false);
      setEditingPayment(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  // v12.8.0: Delete from correct table based on _source
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment record?")) return;
    
    try {
      const payment = payments.find(p => p.id === paymentId);
      const endpoint = payment?._source === 'fee_payments' 
        ? `${API}/fee-payments/${paymentId}` 
        : `${API}/payments/${paymentId}`;
      await axios.delete(endpoint);
      toast.success("Payment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  // v12.5.0: Bulk Generate Annual Renewals (Membership tab only)
  const [renewalGenerating, setRenewalGenerating] = useState(false);
  const [showRenewalDialog, setShowRenewalDialog] = useState(false);
  const [renewalFee, setRenewalFee] = useState("0");

  const handleBulkRenewals = async () => {
    setRenewalGenerating(true);
    try {
      const payload = {
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        class_type: null,
        mark_as_paid: false,
        category: "Membership",
      };
      
      // Override amount in bulk generation: we need a special endpoint or use the existing one
      // The bulk-generate will create unpaid records with default fee
      const response = await axios.post(`${API}/payments/bulk-generate`, payload);
      const { created_count, skipped_count } = response.data;
      
      if (created_count > 0) {
        toast.success(`Created ${created_count} pending Membership renewals${skipped_count > 0 ? `, skipped ${skipped_count} existing` : ''}`);
      } else {
        toast.info(`All ${skipped_count} students already have Membership records for this period`);
      }
      
      setShowRenewalDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to generate renewals");
    } finally {
      setRenewalGenerating(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent("");
    setSelectedMonth("");
    setSelectedYear("");
    setAmount("");
    setPaymentDate("");
    setPaymentMethod("");
    setPaymentStatus("paid");
    setIsPrivateLesson(false);
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true);
    try {
      const payload = {
        month: parseInt(bulkMonth),
        year: parseInt(bulkYear),
        class_type: bulkClassType === "All Classes" ? null : bulkClassType,
        mark_as_paid: bulkMarkAsPaid,
        payment_method: bulkMarkAsPaid ? bulkPaymentMethod : null,
        payment_date: bulkMarkAsPaid ? formatDateForStorage(today) : null,
        category: feeCategory,
      };
      
      const response = await axios.post(`${API}/payments/bulk-generate`, payload);
      
      const { created_count, skipped_count, total_amount } = response.data;
      
      if (created_count > 0) {
        const statusText = bulkMarkAsPaid ? "PAID" : "UNPAID";
        toast.success(
          `Created ${created_count} ${statusText} records (£${total_amount.toFixed(2)})${skipped_count > 0 ? `, skipped ${skipped_count} existing` : ''}`
        );
      } else {
        toast.info(`All ${skipped_count} students already have payments for this month`);
      }
      
      setBulkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to generate payments");
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudent(studentId);
    const student = students.find((s) => s.id === studentId);
    if (student) {
      setAmount(student.monthly_fee?.toString() || "45");
      setSelectedMonth(currentMonth.toString());
      setSelectedYear(currentYear.toString());
    }
  };

  const getStudentName = (studentId) => students.find(s => s.id === studentId)?.name || "Unknown";
  const getStudentClass = (studentId) => students.find(s => s.id === studentId)?.class_type || "";
  const getStudent = (studentId) => students.find(s => s.id === studentId);

  // v7.2: Month navigation
  const goToPrevMonth = () => {
    let m = navMonth - 1, y = navYear;
    if (m < 1) { m = 12; y -= 1; }
    setNavMonth(m); setNavYear(y);
  };
  const goToNextMonth = () => {
    const now = new Date();
    let m = navMonth + 1, y = navYear;
    if (m > 12) { m = 1; y += 1; }
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1)) return;
    setNavMonth(m); setNavYear(y);
  };
  const isCurrentMonth = navMonth === currentMonth && navYear === currentYear;
  const canGoNext = !(navYear === currentYear && navMonth === currentMonth);

  // Determine if payment is "paid" - must have date AND method
  const isPaid = (payment) => payment.payment_date && payment.payment_method;

  // Filter payments — v7.2: always filter by navMonth/navYear, v12.4.0: filter by feeCategory
  // v12.7.2: When searchQuery is active, DISABLE month filter (global search)
  const isSearchActive = searchQuery.trim().length > 0;
  
  const filteredPayments = payments.filter((payment) => {
    const paidStatus = isPaid(payment);
    const matchesStatus = filterStatus === "all" || 
      (filterStatus === "paid" && paidStatus) || 
      (filterStatus === "unpaid" && !paidStatus);
    
    // v12.7.2: Skip month filter when searching
    const matchesMonth = isSearchActive ? true : (payment.month === navMonth && payment.year === navYear);
    
    const matchesClass = filterClass === "all" || getStudentClass(payment.student_id) === filterClass;
    const studentName = getStudentName(payment.student_id).toLowerCase();
    const matchesSearch = searchQuery === "" || studentName.includes(searchQuery.toLowerCase());
    
    // v12.4.0: Category filter
    const paymentCat = payment.category || "Lesson";
    const matchesCategory = paymentCat === feeCategory;
    
    return matchesStatus && matchesMonth && matchesClass && matchesSearch && matchesCategory;
  });

  // Sort by year/month descending, then by name
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    if (a.month !== b.month) return b.month - a.month;
    return getStudentName(a.student_id).localeCompare(getStudentName(b.student_id));
  });

  // Stats - count unpaid (no date/method) for current month
  const unpaidThisMonth = payments.filter(p => 
    p.month === currentMonth && p.year === currentYear && !isPaid(p)
  );
  const unpaidCount = unpaidThisMonth.length;
  const unpaidAmount = unpaidThisMonth.reduce((sum, p) => sum + (p.amount || 0), 0);

  // v7.2: Financial Snapshot for selected nav month (v12.4.0: scoped by category)
  // v12.7.2: When searching, show totals across ALL months for the category
  const monthPayments = isSearchActive
    ? payments.filter(p => (p.category || "Lesson") === feeCategory)
    : payments.filter(p => p.month === navMonth && p.year === navYear && (p.category || "Lesson") === feeCategory);
  const totalCollected = monthPayments.filter(p => isPaid(p)).reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalOutstanding = monthPayments.filter(p => !isPaid(p)).reduce((sum, p) => sum + (p.amount || 0), 0);
  const revenueTarget = totalCollected + totalOutstanding;
  const paidMonthCount = monthPayments.filter(p => isPaid(p)).length;
  const unpaidMonthCount = monthPayments.filter(p => !isPaid(p)).length;

  // Billing icon helper
  const getBillingIcon = (studentId) => {
    const student = getStudent(studentId);
    if (!student) return null;
    if (student.billing_type === "exempt") return <GraduationCap className="w-4 h-4 text-yellow-400 inline ml-1.5" title="Exempt" />;
    if (student.billing_type === "pay_as_you_go") return <Clock className="w-4 h-4 text-sky-400 inline ml-1.5" title="Pay-As-You-Go" />;
    return null;
  };

  // Late payers for Reminders tab (exclude exempt, pay_as_you_go only if specific unpaid record exists)
  const getLatePayers = () => {
    const now = new Date();
    return payments.filter(p => {
      if (p.status !== "unpaid") return false;
      const student = students.find(s => s.id === p.student_id);
      if (!student) return false;
      // Exclude exempt students entirely
      if (student.billing_type === "exempt") return false;
      const dueDate = new Date(p.year, p.month - 1, 1);
      const diffDays = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
      return diffDays >= reminderThreshold;
    }).map(p => {
      const student = students.find(s => s.id === p.student_id);
      const dueDate = new Date(p.year, p.month - 1, 1);
      const diffDays = Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24));
      return {
        ...p,
        studentName: student?.name || "Unknown",
        classTypes: student?.class_types?.length ? student.class_types : (student?.class_type ? [student.class_type] : ["Unknown"]),
        daysLate: diffDays,
      };
    }).sort((a, b) => b.daysLate - a.daysLate);
  };
  const latePayersList = getLatePayers();

  // Group by class
  const lateByClass = {};
  latePayersList.forEach(p => {
    p.classTypes.forEach(cls => {
      if (!lateByClass[cls]) lateByClass[cls] = [];
      lateByClass[cls].push(p);
    });
  });

  // WhatsApp message template
  const generateWhatsAppMessage = (payer) => {
    const monthName = MONTH_NAMES[payer.month - 1];
    return `Hi ${payer.studentName.split(' ')[0]}, this is a friendly reminder from Loong Fu 龍虎堂. Your ${monthName} ${payer.year} class fee of \u00A3${parseFloat(payer.amount).toFixed(2)} is still outstanding. Please arrange payment at your earliest convenience. Thank you!`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Message copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  return (
    <div data-testid="payments-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Payment Ledger 課程收費記錄
          </h1>
          <p className="text-[#A1A1AA] mt-1">Track monthly payment records — <span className="font-semibold" style={{ color: FEE_CATEGORIES.find(c => c.key === feeCategory)?.color }}>{FEE_CATEGORIES.find(c => c.key === feeCategory)?.label}</span></p>
        </div>

        <div className="flex gap-2">
          {/* Bulk Generate Dialog */}
          <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-semibold uppercase tracking-wider rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Zap className="w-4 h-4 mr-2" />
                Generate Monthly
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Generate Monthly Records
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Month</Label>
                    <Select value={bulkMonth} onValueChange={setBulkMonth}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {MONTH_NAMES.map((name, idx) => (
                          <SelectItem key={idx} value={(idx + 1).toString()} className="text-white hover:bg-white/10">{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Year</Label>
                    <Select value={bulkYear} onValueChange={setBulkYear}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {Array.from({ length: 5 }, (_, i) => currentYear - 1 + i).map(year => (
                          <SelectItem key={year} value={year.toString()} className="text-white hover:bg-white/10">{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Class Filter</Label>
                  <Select value={bulkClassType} onValueChange={setBulkClassType}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {CLASS_TYPES.map(type => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-white/10">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Mark as Paid Toggle */}
                <div className="bg-white/5 p-4 rounded-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-white font-medium">Mark as Paid</Label>
                      <p className="text-[#A1A1AA] text-sm">Set today's date and payment method</p>
                    </div>
                    <Switch checked={bulkMarkAsPaid} onCheckedChange={setBulkMarkAsPaid} />
                  </div>
                  
                  {bulkMarkAsPaid && (
                    <div className="pt-2 border-t border-white/10">
                      <Label className="text-[#A1A1AA] text-sm">Payment Method</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setBulkPaymentMethod("cash")}
                          className={`h-12 flex items-center justify-center gap-2 ${
                            bulkPaymentMethod === "cash" ? "bg-green-500/20 border-green-500 text-green-400" : "bg-white/5 border-white/10 text-[#A1A1AA]"
                          }`}
                        >
                          <Banknote className="w-4 h-4" /> Cash
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setBulkPaymentMethod("online")}
                          className={`h-12 flex items-center justify-center gap-2 ${
                            bulkPaymentMethod === "online" ? "bg-blue-500/20 border-blue-500 text-blue-400" : "bg-white/5 border-white/10 text-[#A1A1AA]"
                          }`}
                        >
                          <Wifi className="w-4 h-4" /> Online
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`p-3 rounded-sm ${bulkMarkAsPaid ? 'bg-green-500/10 border border-green-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                  <p className={`text-sm ${bulkMarkAsPaid ? 'text-green-400' : 'text-yellow-400'}`}>
                    {bulkMarkAsPaid 
                      ? `Records will be created as PAID with today's date (${today}) and ${bulkPaymentMethod.toUpperCase()}`
                      : "Records will be created as UNPAID with no date or method"
                    }
                  </p>
                </div>

                <Button
                  onClick={handleBulkGenerate}
                  disabled={bulkGenerating}
                  className="w-full bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase tracking-wider"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {bulkGenerating ? "Generating..." : `Generate ${MONTH_NAMES[parseInt(bulkMonth) - 1]} ${bulkYear}`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* v12.5.0: Bulk Generate Annual Renewals — Membership tab only */}
          {feeCategory === "Membership" && (
            <Dialog open={showRenewalDialog} onOpenChange={setShowRenewalDialog}>
              <DialogTrigger asChild>
                <Button data-testid="bulk-renewals-btn" variant="outline" className="border-orange-500 text-orange-400 hover:bg-orange-500/10 font-semibold uppercase tracking-wider rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  <Users className="w-4 h-4 mr-2" />
                  Bulk Renewals
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold uppercase tracking-wide text-orange-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Bulk Generate Annual Renewals
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-4 space-y-2">
                    <p className="text-white text-sm">This will create a <strong>pending (unpaid)</strong> Membership record for every active student who doesn't already have one for this period.</p>
                    <p className="text-orange-300 text-xs">Students with existing Membership records will be skipped.</p>
                  </div>
                  <Button
                    data-testid="confirm-bulk-renewals-btn"
                    onClick={handleBulkRenewals}
                    disabled={renewalGenerating}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold uppercase tracking-wider"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    {renewalGenerating ? "Generating..." : "Generate Renewal Records"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Add Payment Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold uppercase tracking-wider rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Plus className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Record {feeCategory === "Lesson" ? "" : feeCategory + " "}Payment
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Paid / Unpaid Toggle */}
                <div data-testid="payment-status-toggle" className="flex rounded-sm overflow-hidden border border-white/10">
                  <button
                    type="button"
                    onClick={() => setPaymentStatus("paid")}
                    className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
                      paymentStatus === "paid" 
                        ? "bg-green-500/20 text-green-400 border-b-2 border-green-400" 
                        : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                    }`}
                  >
                    <Check className="w-4 h-4 inline mr-1.5" />Paid
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentStatus("unpaid")}
                    className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
                      paymentStatus === "unpaid" 
                        ? "bg-red-500/20 text-red-400 border-b-2 border-red-400" 
                        : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                    }`}
                  >
                    <X className="w-4 h-4 inline mr-1.5" />Unpaid
                  </button>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Student *</Label>
                  <Select value={selectedStudent} onValueChange={handleStudentSelect}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select student" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10 max-h-60">
                      {students.map(student => (
                        <SelectItem key={student.id} value={student.id} className="text-white hover:bg-white/10">
                          {student.name} ({student.class_type?.replace(" Kung Fu", "")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">For Month *</Label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {MONTH_NAMES.map((month, index) => (
                          <SelectItem key={index + 1} value={(index + 1).toString()} className="text-white hover:bg-white/10">{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Year *</Label>
                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {Array.from({ length: 5 }, (_, i) => currentYear - 1 + i).map(year => (
                          <SelectItem key={year} value={year.toString()} className="text-white hover:bg-white/10">{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Amount ({"\u00A3"}) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="payment-amount-input"
                    value={amount}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                    }}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="45.00"
                  />
                </div>

                {paymentStatus === "paid" && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Payment Date *</Label>
                      <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Payment Method *</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Button type="button" variant="outline" onClick={() => setPaymentMethod("cash")}
                          className={`h-14 flex flex-col items-center justify-center gap-1 ${paymentMethod === "cash" ? "bg-green-500/20 border-green-500 text-green-400" : "bg-white/5 border-white/10 text-[#A1A1AA]"}`}>
                          <Banknote className="w-5 h-5" />
                          <span className="text-xs">Cash</span>
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setPaymentMethod("online")}
                          className={`h-14 flex flex-col items-center justify-center gap-1 ${paymentMethod === "online" ? "bg-blue-500/20 border-blue-500 text-blue-400" : "bg-white/5 border-white/10 text-[#A1A1AA]"}`}>
                          <Wifi className="w-5 h-5" />
                          <span className="text-xs">Online</span>
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {paymentStatus === "unpaid" && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
                    <p className="text-yellow-400 text-sm">Record will be saved as unpaid. You can update the date, amount, and payment method later.</p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                  <Button onClick={handleCreatePayment} className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Record Payment
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Payment Modal */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Update Payment
            </DialogTitle>
          </DialogHeader>
          {editingPayment && (
            <div className="space-y-4 mt-4">
              <div className="bg-white/5 p-3 rounded-sm">
                <p className="text-white font-medium">{getStudentName(editingPayment.student_id)}</p>
                <p className="text-[#A1A1AA] text-sm">{MONTH_NAMES[editingPayment.month - 1]} {editingPayment.year}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Amount (£)</Label>
                <Input type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder="45.00" />
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Payment Date *</Label>
                <Input type="date" value={editPaymentDate} onChange={(e) => setEditPaymentDate(e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Payment Method *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button type="button" variant="outline" onClick={() => setEditPaymentMethod("cash")}
                    className={`h-14 flex flex-col items-center justify-center gap-1 ${editPaymentMethod === "cash" ? "bg-green-500/20 border-green-500 text-green-400" : "bg-white/5 border-white/10 text-[#A1A1AA]"}`}>
                    <Banknote className="w-5 h-5" />
                    <span className="text-xs">Cash</span>
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditPaymentMethod("online")}
                    className={`h-14 flex flex-col items-center justify-center gap-1 ${editPaymentMethod === "online" ? "bg-blue-500/20 border-blue-500 text-blue-400" : "bg-white/5 border-white/10 text-[#A1A1AA]"}`}>
                    <Wifi className="w-5 h-5" />
                    <span className="text-xs">Online</span>
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleSaveEdit} className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  <Check className="w-4 h-4 mr-2" /> Save & Mark Paid
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[#18181b] border border-white/10 p-1">
          <TabsTrigger value="ledger" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
            Ledger
          </TabsTrigger>
          <TabsTrigger value="reminders" data-testid="reminders-tab" className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]">
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            Reminders {latePayersList.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-red-500/20 text-red-400 rounded-full">{latePayersList.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="space-y-4">

      {/* v12.4.0: Fee Category Tabs */}
      <div data-testid="fee-category-tabs" className="flex rounded-sm overflow-hidden border-2 border-[#D4AF37]/30 bg-[#18181b]">
        {FEE_CATEGORIES.map((cat) => {
          const isActive = feeCategory === cat.key;
          const Icon = cat.icon === "Shield" ? Shield : cat.icon === "Award" ? Award : Banknote;
          return (
            <button
              key={cat.key}
              data-testid={`fee-tab-${cat.key.toLowerCase()}`}
              onClick={() => setFeeCategory(cat.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-2 text-sm font-semibold uppercase tracking-wider transition-all ${
                isActive
                  ? "text-black"
                  : "text-[#A1A1AA] hover:bg-white/5"
              }`}
              style={isActive ? { backgroundColor: cat.color, fontFamily: 'Oswald, sans-serif' } : { fontFamily: 'Oswald, sans-serif' }}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{cat.label}</span>
              <span className="sm:hidden">{cat.label.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* v7.2: Month/Year Navigator */}
      <div data-testid="month-navigator" className="flex items-center justify-center gap-3 py-2 relative">
        <Button
          data-testid="prev-month-btn"
          variant="ghost"
          onClick={goToPrevMonth}
          className="h-12 w-12 p-0 rounded-full border border-white/10 hover:bg-white/10 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <button
          data-testid="current-month-label"
          onClick={() => { setQuickJumpYear(navYear); setShowQuickJump(!showQuickJump); }}
          className="text-center min-w-[220px] cursor-pointer hover:opacity-80 transition-opacity"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-[#D4AF37] uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {isSearchActive ? "All Time" : `${MONTH_NAMES[navMonth - 1]} ${navYear}`}
          </h2>
          {isSearchActive ? (
            <span className="text-xs text-purple-400 font-semibold tracking-wider uppercase">Global Search Active</span>
          ) : isCurrentMonth ? (
            <span className="text-xs text-emerald-400 font-semibold tracking-wider uppercase">Current Month</span>
          ) : (
            <span className="text-xs text-[#A1A1AA] tracking-wider">Tap to jump</span>
          )}
        </button>
        <Button
          data-testid="next-month-btn"
          variant="ghost"
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className={`h-12 w-12 p-0 rounded-full border border-white/10 text-white ${canGoNext ? 'hover:bg-white/10' : 'opacity-30 cursor-not-allowed'}`}
        >
          <ChevronRight className="w-6 h-6" />
        </Button>

        {/* v7.3: Quick-Jump Grid Overlay */}
        {showQuickJump && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowQuickJump(false)} />
            <div data-testid="quick-jump-overlay" className="absolute top-full mt-2 z-50 bg-[#18181b] border-2 border-[#D4AF37]/30 rounded-sm shadow-2xl shadow-black/50 p-4 w-[320px]">
            {/* Year selector */}
            <div className="flex items-center justify-between mb-3">
              <Button variant="ghost" size="sm" onClick={() => setQuickJumpYear(y => y - 1)} className="text-white hover:bg-white/10 h-8 w-8 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-lg font-bold text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>{quickJumpYear}</span>
              <Button variant="ghost" size="sm" onClick={() => setQuickJumpYear(y => Math.min(y + 1, currentYear))} disabled={quickJumpYear >= currentYear} className="text-white hover:bg-white/10 h-8 w-8 p-0 disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {/* Month grid */}
            <div className="grid grid-cols-3 gap-2">
              {MONTH_NAMES.map((name, idx) => {
                const m = idx + 1;
                const isFuture = quickJumpYear > currentYear || (quickJumpYear === currentYear && m > currentMonth);
                const isSelected = m === navMonth && quickJumpYear === navYear;
                return (
                  <button
                    key={m}
                    disabled={isFuture}
                    data-testid={`quick-jump-${m}-${quickJumpYear}`}
                    onClick={() => { setNavMonth(m); setNavYear(quickJumpYear); setShowQuickJump(false); }}
                    className={`py-2 px-1 text-xs font-semibold rounded-sm transition-all uppercase tracking-wide ${
                      isSelected
                        ? 'bg-[#D4AF37] text-black'
                        : isFuture
                          ? 'text-[#52525b] cursor-not-allowed'
                          : 'text-white hover:bg-white/10 border border-white/5'
                    }`}
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    {name.substring(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
          </>
        )}
      </div>

      {/* v7.2: Financial Snapshot Card */}
      <Card data-testid="financial-snapshot" className="bg-gradient-to-br from-[#18181b] to-[#1f1f23] border-[#D4AF37]/30 border-2 rounded-sm shadow-lg shadow-[#D4AF37]/5">
        <CardContent className="p-5 sm:p-7">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {/* Total Collected */}
            <div data-testid="snapshot-collected" className="flex flex-col items-center justify-center p-5 sm:p-6 rounded-sm" style={{ background: 'rgba(34,139,34,0.12)', border: '1px solid rgba(34,139,34,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-5 h-5" style={{ color: '#228B22' }} />
                <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#228B22', fontFamily: 'Oswald, sans-serif' }}>Collected</span>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                &pound;{totalCollected.toFixed(2)}
              </p>
              <span className="text-xs mt-1" style={{ color: '#228B22' }}>{paidMonthCount} paid</span>
            </div>

            {/* Total Outstanding */}
            <div data-testid="snapshot-outstanding" className="flex flex-col items-center justify-center p-5 sm:p-6 rounded-sm" style={{ background: 'rgba(220,20,60,0.12)', border: '1px solid rgba(220,20,60,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5" style={{ color: '#DC143C' }} />
                <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#DC143C', fontFamily: 'Oswald, sans-serif' }}>Outstanding</span>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                &pound;{totalOutstanding.toFixed(2)}
              </p>
              <span className="text-xs mt-1" style={{ color: '#DC143C' }}>{unpaidMonthCount} unpaid</span>
            </div>

            {/* Revenue Target */}
            <div data-testid="snapshot-target" className="flex flex-col items-center justify-center p-5 sm:p-6 rounded-sm" style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>Revenue Target</span>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                &pound;{revenueTarget.toFixed(2)}
              </p>
              <span className="text-xs mt-1 text-[#D4AF37]">{monthPayments.length} records</span>
            </div>
          </div>

          {/* Collection Progress Bar */}
          {revenueTarget > 0 && (
            <div className="mt-5">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-[#A1A1AA]">Collection Progress</span>
                <span className="text-white font-semibold">{Math.round((totalCollected / revenueTarget) * 100)}%</span>
              </div>
              <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round((totalCollected / revenueTarget) * 100))}%`, background: 'linear-gradient(90deg, #228B22, #32CD32)' }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-[#18181b] border-white/10 rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input data-testid="search-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white" placeholder="Search by student name..." />
            </div>
            
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger data-testid="filter-class" className="w-full lg:w-44 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Filter by class" />
              </SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Classes</SelectItem>
                <SelectItem value="Tigers Kung Fu" className="text-white hover:bg-white/10">Tigers</SelectItem>
                <SelectItem value="Dragons Kung Fu" className="text-white hover:bg-white/10">Dragons</SelectItem>
                <SelectItem value="Adults Kung Fu" className="text-white hover:bg-white/10">Adults</SelectItem>
                <SelectItem value="Tai Chi" className="text-white hover:bg-white/10">Tai Chi</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status" className="w-full lg:w-36 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Status</SelectItem>
                <SelectItem value="paid" className="text-white hover:bg-white/10">Paid</SelectItem>
                <SelectItem value="unpaid" className="text-white hover:bg-white/10">Unpaid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {(searchQuery || filterClass !== "all" || filterStatus !== "all") && (
            <p className="text-[#A1A1AA] text-sm mt-3">Showing {sortedPayments.length} of {monthPayments.length} payments{isSearchActive ? ` matching "${searchQuery}"` : ` for ${MONTH_NAMES[navMonth - 1]}`}</p>
          )}
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card className="bg-[#18181b] border-white/10 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Student</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Class</TableHead>
                  {isSearchActive && <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Period</TableHead>}
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Date Paid</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Method</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#A1A1AA]">Loading...</TableCell></TableRow>
                ) : sortedPayments.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#A1A1AA]">{isSearchActive ? `No records matching "${searchQuery}"` : `No payment records for ${MONTH_NAMES[navMonth - 1]} ${navYear}`}</TableCell></TableRow>
                ) : (
                  sortedPayments.map((payment) => {
                    const paid = isPaid(payment);
                    return (
                      <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`} className="border-white/5 hover:bg-white/5">
                        <TableCell className="font-medium text-white">
                          <span className="inline-flex items-center gap-1">
                            {payment.is_private_lesson && <User className="w-4 h-4 text-[#D4AF37] mr-0.5" title="Private Lesson" />}
                            {getStudentName(payment.student_id)}
                            {getBillingIcon(payment.student_id)}
                            {payment._legacy && <span className="px-1.5 py-0.5 text-[9px] rounded bg-yellow-500/20 text-yellow-400 font-bold uppercase tracking-wider">Legacy</span>}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-0.5 text-xs rounded-sm ${
                            getStudentClass(payment.student_id).includes("Tigers") ? "bg-orange-500/20 text-orange-400" :
                            getStudentClass(payment.student_id).includes("Dragons") ? "bg-blue-500/20 text-blue-400" :
                            getStudentClass(payment.student_id).includes("Tai Chi") ? "bg-teal-500/20 text-teal-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {getStudentClass(payment.student_id).replace(" Kung Fu", "") || "\u2014"}
                          </span>
                        </TableCell>
                        {isSearchActive && (
                          <TableCell className="text-[#A1A1AA] text-xs">
                            {MONTH_NAMES[payment.month - 1]?.substring(0, 3)} {payment.year}
                          </TableCell>
                        )}
                        <TableCell className="text-[#A1A1AA]">{formatDateForDisplay(payment.payment_date)}</TableCell>
                        <TableCell className="text-white font-mono">&pound;{(payment.amount || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          {payment.payment_method ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-sm ${
                              payment.payment_method === "cash" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"
                            }`}>
                              {payment.payment_method === "cash" ? <Banknote className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
                              {payment.payment_method === "cash" ? "Cash" : "Online"}
                            </span>
                          ) : <span className="text-[#A1A1AA]">{"\u2014"}</span>}
                        </TableCell>
                        <TableCell>
                          {paid ? (
                            <span
                              data-testid={`status-paid-${payment.id}`}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-sm uppercase tracking-wider"
                              style={{ background: '#228B22', color: '#FFFFFF' }}
                            >
                              <Check className="w-3.5 h-3.5" /> Paid
                            </span>
                          ) : (
                            <Button
                              data-testid={`status-unpaid-${payment.id}`}
                              size="sm"
                              onClick={() => handleEditPayment(payment)}
                              className="h-8 px-4 text-xs font-bold uppercase tracking-wider rounded-sm"
                              style={{ background: '#DC143C', color: '#FFFFFF' }}
                            >
                              <X className="w-3.5 h-3.5 mr-1" /> Unpaid
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`edit-payment-${payment.id}`} size="sm" variant="ghost" onClick={() => handleEditPayment(payment)} className="text-[#D4AF37] hover:bg-[#D4AF37]/10 h-7 w-7 p-0">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button data-testid={`delete-payment-${payment.id}`} size="sm" variant="ghost" onClick={() => handleDeletePayment(payment.id)} className="text-[#ef4444] hover:bg-red-500/10 h-7 w-7 p-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" data-testid="reminders-tab-content" className="space-y-4">
          {/* Threshold Config */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#ef4444]" />
                  <div>
                    <p className="text-white font-semibold text-sm">Late Payment Reminders</p>
                    <p className="text-[#A1A1AA] text-xs">Students with unpaid records older than threshold</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#A1A1AA] text-xs">Overdue after</span>
                  <select
                    data-testid="payments-reminder-threshold"
                    value={reminderThreshold}
                    onChange={e => {
                      const val = parseInt(e.target.value);
                      setReminderThreshold(val);
                      axios.put(`${API}/settings/reminder_threshold_days`, { value: val }).catch(() => {});
                    }}
                    className="bg-white/5 border border-white/10 rounded text-white text-xs px-2 py-1.5 focus:outline-none"
                  >
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="21">21 days</option>
                    <option value="30">30 days</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {latePayersList.length === 0 ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-white font-semibold">All Caught Up</p>
                <p className="text-[#A1A1AA] text-sm mt-1">No late payments beyond {reminderThreshold} days</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(lateByClass).map(([cls, payers]) => (
                  <Card key={cls} className="bg-[#18181b] border-white/10 rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          cls.includes("Tigers") ? "bg-orange-400" :
                          cls.includes("Dragons") ? "bg-blue-400" :
                          cls.includes("Tai Chi") ? "bg-teal-400" :
                          cls.includes("Private") ? "bg-[#D4AF37]" :
                          "bg-red-500"
                        }`} />
                        <span className="text-white text-sm font-semibold">{cls.replace(" Kung Fu", "")}</span>
                      </div>
                      <p className="text-2xl font-bold text-[#ef4444]" style={{ fontFamily: 'Oswald, sans-serif' }}>{payers.length}</p>
                      <p className="text-[#A1A1AA] text-xs">&pound;{payers.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0).toFixed(2)} owed</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Grouped Tables */}
              {Object.entries(lateByClass).map(([cls, payers]) => (
                <Card key={cls} className="bg-[#18181b] border-white/10 rounded-sm">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-4 border-b border-white/5">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          cls.includes("Tigers") ? "bg-orange-400" :
                          cls.includes("Dragons") ? "bg-blue-400" :
                          cls.includes("Tai Chi") ? "bg-teal-400" :
                          cls.includes("Private") ? "bg-[#D4AF37]" :
                          "bg-red-500"
                        }`} />
                        <h3 className="text-white font-semibold uppercase tracking-wide text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>{cls}</h3>
                        <span className="text-[#A1A1AA] text-xs">({payers.length} late)</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/5 hover:bg-transparent">
                            <TableHead className="text-[#A1A1AA] text-xs uppercase">Student</TableHead>
                            <TableHead className="text-[#A1A1AA] text-xs uppercase">Period</TableHead>
                            <TableHead className="text-[#A1A1AA] text-xs uppercase">Amount</TableHead>
                            <TableHead className="text-[#A1A1AA] text-xs uppercase">Days Late</TableHead>
                            <TableHead className="text-[#A1A1AA] text-xs uppercase text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {payers.map((p) => (
                            <TableRow key={`${p.id}-${cls}`} className="border-white/5 hover:bg-white/5">
                              <TableCell className="font-medium text-white">{p.studentName}</TableCell>
                              <TableCell className="text-white">{MONTH_NAMES[p.month - 1]} {p.year}</TableCell>
                              <TableCell className="text-white font-mono">&pound;{parseFloat(p.amount).toFixed(2)}</TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-sm ${
                                  p.daysLate >= 30 ? "bg-red-500/20 text-red-400" :
                                  p.daysLate >= 14 ? "bg-orange-500/20 text-orange-400" :
                                  "bg-yellow-500/20 text-yellow-400"
                                }`}>
                                  <Clock className="w-3 h-3" /> {p.daysLate}d
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    data-testid={`copy-msg-${p.id}`}
                                    onClick={() => copyToClipboard(generateWhatsAppMessage(p))} 
                                    className="text-[#22c55e] hover:bg-green-500/10 text-xs h-7"
                                  >
                                    <MessageSquare className="w-3 h-3 mr-1" /> Copy Msg
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => handleEditPayment(p)} 
                                    className="text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs h-7"
                                  >
                                    <Pencil className="w-3 h-3 mr-1" /> Pay
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
